import React from 'react';
import { Column } from 'design-system-web';
import {
  ChargeBillingReport,
  SortBy as gqlSort,
  SortDirection as gqlSortDirection,
} from 'graphql-query-contracts';

import { dates } from 'lane-shared/helpers/constants';
import { dateFormatter } from 'lane-shared/helpers/formatters';
import { formatCurrencyWithSymbolOnly, formatCurrencyNoSymbol } from 'domains-billing-currency';
import {
  graphQLToCurrency,
  currencyToPaymentCurrency,
} from 'domains-billing-web-mappers';
import { SortDirectionEnum } from 'constants-activate';


export function getChargeBillingReportColumns(
  t: any,
  charge_month_format: string,
  timeZone?: string,
  threeLetterAbbrev?: string,
  locale?: string,
): Column<ChargeBillingReport>[] {

  return [
    {
      key: 'payer.id',
      header: t('abp.chargeBillingReports.customer'),
      renderForCSV: (cell: string, row) => {
        return row.payer?.id || cell;
      },
      disableSorting: true,
      disableVisibilityToggle: true,
    },
    {
      key: 'name',
      header: t('abp.chargeBillingReports.name'),
    },
    {
      key: 'description',
      header: t('abp.chargeBillingReports.description'),
      disableSorting: true,
    },
    {
      key: 'chargeCode',
      header: t('abp.chargeBillingReports.chargeCode'),
    },
    {
      key: 'currency',
      header: t('abp.chargeBillingReports.currency'),
      renderCell: (cell: number, row) => (
        <span>
          {currencyToPaymentCurrency(row.currency)}
        </span>
      ),
      renderForCSV: (cell: number, row) => {
        return currencyToPaymentCurrency(row.currency);
      },
    },
    {
      key: 'totalAmount',
      header: t('abp.chargeBillingReports.totalAmount'),
      renderCell: (cell: number, row) => {
        const domainCurrency = graphQLToCurrency(row.currency);

        return (
          <span>
            {formatCurrencyWithSymbolOnly(domainCurrency, cell, locale)}
          </span>
        );
      },
      renderForCSV: (cell: number, row) => {
        const domainCurrency = graphQLToCurrency(row.currency);

        return `${formatCurrencyNoSymbol(domainCurrency, cell, locale)}`;
      },
    },
    {
      key: 'createdAt',
      header: `${t('abp.chargeBillingReports.createdDate')}${threeLetterAbbrev ? ` ${threeLetterAbbrev}` : ''
        }`,
      renderCell: (cell: string) => (
        <span>{dateFormatter(cell, dates.SIMPLE_DATE, timeZone)}</span>
      ),
      renderForCSV: (cell: string, _) => {
        return dateFormatter(cell, dates.DATE_ISO_8601, timeZone);
      },
    },
    {
      key: 'entity.type',
      header: t('abp.chargeBillingReports.entityType'),
      renderCell: (cell: string) => t(`abp.entityType.${cell}`),
      renderForCSV: (cell: string, _) => {
        return cell;
      },
      disableSorting: true,
    },
    {
      key: 'entityDisplayId',
      header: t('abp.chargeBillingReports.entityDisplayId'),
      disableSorting: true,
    },
    {
      key: 'entityDisplayType',
      header: t('abp.chargeBillingReports.entityDisplayType'),
      disableSorting: true,
    },
    {
      key: 'glCode',
      header: t('abp.chargeBillingReports.glCode'),
    },
    {
      key: 'leaseId',
      header: t('abp.chargeBillingReports.leaseId'),
      disableSorting: true,
    },
    {
      key: 'propertyId',
      header: t('abp.chargeBillingReports.propertyId'),
      disableSorting: true,
    },
    {
      key: 'suiteId',
      header: t('abp.chargeBillingReports.suiteId'),
      disableSorting: true,
    },
    {
      key: 'chargeMonth',
      header: `${t('abp.chargeBillingReports.createdMonth')}${threeLetterAbbrev ? ` ${threeLetterAbbrev}` : ''
        }`,
      renderCell: (cell: string) => (
        <span>{dateFormatter(cell, 'MMM 1, yyyy', timeZone)}</span>
      ),
      renderForCSV: (cell: string, _) => {
        return dateFormatter(cell, charge_month_format, timeZone);
      },
    },
  ];
}

export function normalizeSort(
  key: string,
  direction: gqlSortDirection | SortDirectionEnum
): gqlSort {
  const sort: gqlSort = {
    dir: normalizeDirection(direction),
    key: normalizeSortKey(key),
  };

  return sort;
}

function normalizeDirection(
  direction: gqlSortDirection | SortDirectionEnum
): gqlSortDirection {
  switch (direction) {
    case SortDirectionEnum.Asc:
      return gqlSortDirection.Asc;
    case SortDirectionEnum.Desc:
      return gqlSortDirection.Desc;
    default:
      return gqlSortDirection.Asc;
  }
}

function normalizeSortKey(key: string): string {
  switch (key) {
    case 'name':
      return 'name';
    case 'chargeCode':
      return 'charge_code';
    case 'glCode':
      return 'gl_code';
    case 'totalAmount':
      return 'amount';
    default:
      return 'created_at';
  }
}
